import React, { useCallback, useEffect, useState } from "react";
import {
    Dialog,
    DialogType,
    DialogFooter,
    DefaultButton,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    mergeStyleSets,
} from "@fluentui/react";
import { IGameEntry, ITeam } from "./LiveScoreboard";

interface IScoresheetModalProps {
    gameKey: string;
    game: IGameEntry;
    teamDictionary: Record<string, ITeam>;
    onDismiss: () => void;
}

interface IQuestionCellData {
    buzzValue: number;
    playerInitials: string;
    bonusParts?: boolean[];
    bonusTotal?: number;
}

function getInitials(name: string): string {
    return name
        .split(/\s+/)
        .map(part =>
            /^[A-Za-z]/.test(part) ?
                part.charAt(0).toUpperCase() : "")
        .join("");
}

function computeTeamQuestionData(matchQuestions: any[], teamName: string): (IQuestionCellData | null)[] {
    return matchQuestions.map(q => {
        const buzzes: any[] = q.buzzes ?? [];
        const teamBuzz = buzzes.find((b: any) => b.team?.name === teamName);
        if (!teamBuzz) return null;

        const buzzValue: number = teamBuzz.result?.value ?? 0;
        const playerName: string = teamBuzz.player?.name ?? "";
        const playerInitials = getInitials(playerName);

        const result: IQuestionCellData = { buzzValue, playerInitials };

        if (buzzValue > 0 && q.bonus) {
            const parts: boolean[] = (q.bonus.parts ?? []).map(
                (part: any) => (part.controlled_points ?? 0) > 0
            );
            const bonusTotal = parts.reduce((sum: number, correct: boolean) => sum + (correct ? 10 : 0), 0);
            result.bonusParts = parts;
            result.bonusTotal = bonusTotal;
        }

        return result;
    });
}

const styles = mergeStyleSets({
    tableContainer: {
        marginTop: 16,
        overflowX: "auto" as const,
        WebkitOverflowScrolling: "touch" as const,
        touchAction: "pan-x pan-y",
    },
    table: {
        borderCollapse: "collapse" as const,
        fontSize: 13,
    },
    headerCell: {
        border: "1px solid #d1d1d1",
        padding: "4px 8px",
        backgroundColor: "#f3f2f1",
        fontWeight: 600,
        textAlign: "center" as const,
        minWidth: 36,
        whiteSpace: "nowrap" as const,
    },
    teamNameCell: {
        border: "1px solid #d1d1d1",
        padding: "6px 10px",
        fontWeight: 600,
        whiteSpace: "nowrap" as const,
        backgroundColor: "#fafafa",
    },
    cell: {
        border: "1px solid #d1d1d1",
        padding: "4px 6px",
        textAlign: "center" as const,
        minWidth: 36,
        verticalAlign: "top" as const,
        fontSize: 12,
        lineHeight: "1.4",
    },
    cellPositive: {
        backgroundColor: "#dff6dd",
    },
    cellNegative: {
        backgroundColor: "#fde7e9",
    },
    cellZero: {
        color: "#a19f9d",
    },
    buzzLine: {
        whiteSpace: "nowrap" as const,
    },
    bonusLine: {
        whiteSpace: "nowrap" as const,
        fontSize: 11,
    },
    correctMark: {
        color: "#107c10",
        fontWeight: "bold" as const,
    },
    wrongMark: {
        color: "#d13438",
        fontWeight: "bold" as const,
    },
    totalCell: {
        border: "1px solid #d1d1d1",
        padding: "6px 10px",
        textAlign: "center" as const,
        fontWeight: 700,
        backgroundColor: "#f3f2f1",
        minWidth: 52,
    },
    playerTablesContainer: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 20,
        marginTop: 20,
        "@media (max-width: 600px)": {
            gridTemplateColumns: "1fr",
        },
    },
    playerTableSection: {
        minWidth: 0,
    },
    playerTeamName: {
        fontSize: 15,
        fontWeight: 700,
        marginBottom: 6,
    },
    playerTable: {
        borderCollapse: "collapse" as const,
        fontSize: 13,
        width: "100%",
    },
    playerCell: {
        border: "1px solid #d1d1d1",
        padding: "4px 8px",
        textAlign: "center" as const,
    },
    playerNameCell: {
        border: "1px solid #d1d1d1",
        padding: "4px 8px",
        textAlign: "left" as const,
        whiteSpace: "nowrap" as const,
    },
    gameInfo: {
        fontSize: 13,
        color: "#605e5c",
        marginBottom: 8
    },
});

export default function ScoresheetModal({ gameKey, game, teamDictionary, onDismiss }: IScoresheetModalProps) {
    const [gameJson, setGameJson] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchGame = useCallback(() => {
        setLoading(true);
        setError(null);
        fetch(`/api/games/${gameKey}`)
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then(data => {
                setGameJson(data);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, [gameKey]);

    useEffect(() => {
        fetchGame();
    }, [fetchGame]);

    const teamEntries = Object.entries(game.teams);
    const [teamAId] = teamEntries[0] ?? [""];
    const [teamBId] = teamEntries[1] ?? [""];
    const teamAName = teamDictionary[teamAId]?.name ?? teamAId;
    const teamBName = teamDictionary[teamBId]?.name ?? teamBId;

    // Use team names from the raw JSON for point computation (matches buzz records)
    const rawTeamAName: string = gameJson?.match_teams?.[0]?.team?.name ?? teamAName;
    const rawTeamBName: string = gameJson?.match_teams?.[1]?.team?.name ?? teamBName;

    const matchQuestions: any[] = gameJson?.match_questions ?? [];

    const teamACells = computeTeamQuestionData(matchQuestions, rawTeamAName);
    const teamBCells = computeTeamQuestionData(matchQuestions, rawTeamBName);
    const teamATotal = teamACells.reduce((sum, d) => {
        if (!d) return sum;
        return sum + d.buzzValue + (d.bonusTotal ?? 0);
    }, 0);
    const teamBTotal = teamBCells.reduce((sum, d) => {
        if (!d) return sum;
        return sum + d.buzzValue + (d.bonusTotal ?? 0);
    }, 0);

    const renderCell = (data: IQuestionCellData | null, key: string) => {
        if (data === null) {
            return <td key={key} className={styles.cell} />;
        }

        let cellStyle = styles.cell;
        if (data.buzzValue > 0) {
            cellStyle = `${styles.cell} ${styles.cellPositive}`;
        } else if (data.buzzValue < 0) {
            cellStyle = `${styles.cell} ${styles.cellNegative}`;
        } else {
            cellStyle = `${styles.cell} ${styles.cellZero}`;
        }

        const buzzDisplay = data.buzzValue + (data.playerInitials ? ` (${data.playerInitials})` : "");
        const buzzClass = data.buzzValue > 0 ? styles.correctMark : data.buzzValue < 0 ? styles.wrongMark : undefined;

        return (
            <td key={key} className={cellStyle}>
                <div className={styles.buzzLine}>
                    <span className={buzzClass}>{buzzDisplay}</span>
                </div>
                {data.bonusParts && (
                    <div className={styles.bonusLine}>
                        {data.bonusParts.map((correct, i) => (
                            <span key={i} className={correct ? styles.correctMark : styles.wrongMark}>
                                {correct ? "✓" : "✗"}
                            </span>
                        ))}
                        {" "}({data.bonusTotal})
                    </div>
                )}
            </td>
        );
    };

    const titleParts: string[] = [];
    if (game.round) {
        titleParts.push(game.round);
    }

    titleParts.push(`${teamAName} vs. ${teamBName}`);
    const title = titleParts.join(" · ");

    return (
        <Dialog
            hidden={false}
            onDismiss={onDismiss}
            minWidth={680}
            maxWidth="95vw"
            dialogContentProps={{
                type: DialogType.normal,
                title,
            }}
            modalProps={{
                isBlocking: false,
                styles: {
                    scrollableContent: {
                        touchAction: "pan-x pan-y",
                    },
                },
            }}
        >
            {loading && <Spinner size={SpinnerSize.large} label="Loading scoresheet..." />}
            {error && (
                <MessageBar messageBarType={MessageBarType.error}>
                    Failed to load scoresheet: {error}
                </MessageBar>
            )}
            {!loading && !error && (
                <>
                    {game.moderator && (
                        <div className={styles.gameInfo}>
                            <strong>Moderator(s):</strong> {game.moderator}
                        </div>
                    )}
                    {game.startTime && (
                        <div className={styles.gameInfo}>
                            <strong>Start Time:</strong> {new Date(game.startTime).toLocaleString()}
                        </div>
                    )}
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.headerCell}>Team</th>
                                    {matchQuestions.map(q => (
                                        <th key={q.question_number} className={styles.headerCell}>
                                            {q.question_number}
                                        </th>
                                    ))}
                                    <th className={styles.headerCell}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={styles.teamNameCell}>{teamAName}</td>
                                    {teamACells.map((d, i) => renderCell(d, `a-${i}`))}
                                    <td className={styles.totalCell}>{teamATotal}</td>
                                </tr>
                                <tr>
                                    <td className={styles.teamNameCell}>{teamBName}</td>
                                    {teamBCells.map((d, i) => renderCell(d, `b-${i}`))}
                                    <td className={styles.totalCell}>{teamBTotal}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.playerTablesContainer}>
                        {(gameJson?.match_teams ?? []).map((matchTeam: any) => {
                            const teamName: string = matchTeam?.team?.name ?? "Unknown";
                            const matchPlayers: any[] = matchTeam?.match_players ?? [];
                            return (
                                <div key={teamName} className={styles.playerTableSection}>
                                    <div className={styles.playerTeamName}>{teamName}</div>
                                    <table className={styles.playerTable}>
                                        <thead>
                                            <tr>
                                                <th className={styles.headerCell}>Player</th>
                                                <th className={styles.headerCell}>10</th>
                                                <th className={styles.headerCell}>-5</th>
                                                <th className={styles.headerCell}>Pts</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {matchPlayers.map((mp: any) => {
                                                const name: string = mp.player?.name ?? "";
                                                const counts: any[] = mp.answer_counts ?? [];
                                                const tens = counts.find((c: any) => c.answer?.value === 10)?.number ?? 0;
                                                const negs = counts.find((c: any) => c.answer?.value === -5)?.number ?? 0;
                                                const pts = tens * 10 + negs * -5;
                                                return (
                                                    <tr key={name}>
                                                        <td className={styles.playerNameCell}>{name}</td>
                                                        <td className={styles.playerCell}>{tens}</td>
                                                        <td className={styles.playerCell}>{negs}</td>
                                                        <td className={styles.playerCell}>{pts}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
            <DialogFooter>
                <PrimaryButton onClick={fetchGame} text="Refresh" disabled={loading} />
                <DefaultButton onClick={onDismiss} text="Close" />
            </DialogFooter>
        </Dialog>
    );
}
